import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import { ArrowRight, BookOpen, Users, Target, Award } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import Hls from "hls.js";
import * as React from "react";

const VideoPlayer = ({ className }: { className?: string }) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [videoSrc, setVideoSrc] = React.useState<string>('');
  const [videoPoster, setVideoPoster] = React.useState<string>("./images/公司图片.png");
  
  React.useEffect(() => {
    let hls: Hls | null = null;
    
    const fetchVideoUrl = async () => {
      try {
        // 导入API模块
        const { caseVideoApi } = await import('@/api/modules/caseVideo');
        
        // 调用API获取视频URL
        const response = await caseVideoApi.getM3u8Url();
        
        if (response.code === 200 && response.data) {
          const videoUrl = response.data.url;
          const coverUrl = response.data.cover;
          
          // 如果是相对路径，转换为完整URL
          // API请求使用VITE_API_BASE_URL，但视频和封面使用单独配置的地址
          const videoBaseUrl = import.meta.env.VITE_VIDEO_BASE_URL || import.meta.env.VITE_API_BASE_URL || '';
          const coverBaseUrl = import.meta.env.VITE_COVER_BASE_URL || import.meta.env.VITE_API_BASE_URL || '';
          
          const fullVideoUrl = videoUrl.startsWith('/') ? `${videoBaseUrl}${videoUrl}` : videoUrl;
          const fullCoverUrl = coverUrl.startsWith('/') ? `${coverBaseUrl}${coverUrl}` : coverUrl;
          
          // 更新海报图片为API返回的封面
          setVideoPoster(fullCoverUrl);
          
          if (videoRef.current) {
            // 检查浏览器是否原生支持HLS
            if (videoRef.current.canPlayType('application/vnd.apple.mpegurl') || videoRef.current.canPlayType('application/x-mpegURL')) {
              // Safari原生支持HLS
              videoRef.current.src = fullVideoUrl;
            } else if (Hls.isSupported()) {
              // 使用hls.js播放m3u8
              hls = new Hls();
              hls.loadSource(fullVideoUrl);
              hls.attachMedia(videoRef.current);
            }
          }
          
          setVideoSrc(fullVideoUrl);
        }
      } catch (error) {
        console.error('获取视频URL失败:', error);
      }
    };
    
    fetchVideoUrl();
    
    return () => {
      if (hls) {
        hls.destroy();
      }
    };
  }, []);
  
  return (
    <video
      ref={videoRef}
      controls
      className={className || "w-full h-auto object-cover rounded-2xl"}
      poster={videoPoster}
    />
  );
};

const About = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              杭州光智云软件有限公司简介
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              专注于光学智能算法，致力于光学软件研发的高新技术公司
            </p>
          </div>
        </div>
      </section>

      {/* Company Introduction */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                公司背景
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                杭州光智云软件有限公司依托于中国科学院软件所和杭州光学精密机械研究所（杭光所由中国科学院上光所与杭州市富阳区人民政府共建，定位为企业孵化器平台），是一家以光学智能算法为核心，专注于光学软件研发的高新技术公司。
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                光智云基于物理机理进行智能信息处理，采用模型、数据双驱动研究范式，将传统光学和现代 AI 技术充分融合，拥有遥感图像智能处理、光场智能控制等核心技术。
              </p>
            </div>
            <div className="flex justify-center">
              <div className="bg-gradient-to-r from-blue-100 to-blue-200 rounded-2xl p-8 w-full h-80 flex items-center justify-center">
                <div className="bg-white rounded-xl p-6 shadow-lg w-full h-full flex items-center justify-center">
                  <div className="text-center">
                    <BookOpen className="w-16 h-16 text-blue-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900">光学智能算法</h3>
                    <p className="text-gray-600 mt-2">核心技术</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Carousel Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">了解我们的光学仿真软件</h2>
            {/* <h2 className="text-2xl font-bold text-gray-900 mb-2">产品演示</h2> */}
            {/* <p className="text-gray-600">了解我们的光学仿真软件</p> */}
          </div>
          <div className="flex justify-center">
            <div className="w-full max-w-4xl">
              <Carousel opts={{ loop: true }} plugins={[Autoplay({ delay: 5000 })]} className="w-full overflow-hidden">
                <CarouselContent className="-ml-0">
                  <CarouselItem className="pl-0 basis-full">
                    <div className="relative bg-gray-100 rounded-2xl overflow-hidden aspect-video flex items-center justify-center">
                      <VideoPlayer />
                    </div>
                  </CarouselItem>
                </CarouselContent>
              </Carousel>
            </div>
          </div>
        </div>
      </section>

      {/* Core Technologies */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              核心技术优势
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              拥有遥感图像智能处理、光场智能控制等核心技术，为国防军事、航空航天、高端仪器与装备制造提供重要支撑
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Target className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">遥感图像智能处理</h3>
              <p className="text-gray-600">
                先进的遥感图像分析和处理技术
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Target className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">光场智能控制</h3>
              <p className="text-gray-600">
                高精度光场控制和优化技术
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Target className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">模型数据双驱动</h3>
              <p className="text-gray-600">
                结合物理模型和数据驱动的智能算法
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Target className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">AI光学融合</h3>
              <p className="text-gray-600">
                传统光学与现代AI技术深度融合
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Product Series */}
      {/* <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              See系列软件
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              光智云核心产品线，覆盖光学设计、激光仿真、光纤分析等多个领域
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-2xl">
              <h3 className="text-2xl font-bold text-blue-800 mb-4">SeeOD 光学系统优化设计软件</h3>
              <p className="text-gray-700">
                专业的光学系统设计与优化工具，提供高精度光线追迹和多变量多目标自动优化功能
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-8 rounded-2xl">
              <h3 className="text-2xl font-bold text-green-800 mb-4">SeeLight 高能激光系统仿真软件</h3>
              <p className="text-gray-700">
                专业级激光系统仿真工具，支持复杂激光系统的建模与分析
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-8 rounded-2xl">
              <h3 className="text-2xl font-bold text-purple-800 mb-4">SeeFiber 光纤激光仿真软件</h3>
              <p className="text-gray-700">
                专门针对光纤激光器设计的仿真分析工具，提供精确的光纤光学仿真
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-8 rounded-2xl">
              <h3 className="text-2xl font-bold text-orange-800 mb-4">SeeNano 光纤剖面设计软件</h3>
              <p className="text-gray-700">
                专业光纤剖面设计工具，支持复杂光纤结构的设计与优化
              </p>
            </div>
          </div>
        </div>
      </section> */}

      {/* Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              服务与合作
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto whitespace-nowrap">
              光智云提供优质软件产品的同时 为客户提供专业的技术支持和售后服务
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                我们的服务
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center mt-1">
                    <div className="w-1 h-1 bg-white rounded-full"></div>
                  </div>
                  <p className="ml-3 text-gray-600">专业软件产品供应</p>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center mt-1">
                    <div className="w-1 h-1 bg-white rounded-full"></div>
                  </div>
                  <p className="ml-3 text-gray-600">专业技术支持</p>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center mt-1">
                    <div className="w-1 h-1 bg-white rounded-full"></div>
                  </div>
                  <p className="ml-3 text-gray-600">各类光学设计项目承接</p>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center mt-1">
                    <div className="w-1 h-1 bg-white rounded-full"></div>
                  </div>
                  <p className="ml-3 text-gray-600">联合研发合作</p>
                </li>
              </ul>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                合作伙伴
              </h3>
              <p className="text-gray-600 mb-6">
                已与国内领先高等院校、研究院所以及行业龙头企业进行联合研发合作，基于合作伙伴在工程及需求方面的经验，实现产品针对性的升级迭代，为国防军事、航空航天、高端仪器与装备制造的持续创新提供重要支撑。
              </p>
              <div className="flex items-center text-blue-600">
                <Users className="w-5 h-5 mr-2" />
                <span>产学研合作</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            了解我们的产品与服务
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            探索光学仿真软件，体验智能算法带来的效率提升
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild className="bg-white text-blue-600 hover:bg-blue-50">
              <Link to="/products">
                了解产品 <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild className="border-white text-white hover:bg-white/10">
              <Link to="/contact">
                联系我们
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default About;